import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  $Tag,
  $AmountLabel,
  $DateLabel,
  $Tooltip,
  $Button,
  $Popconfirm,
  $TextArea,
} from "us.common/components";
import { IPaymentStates } from "us.collection.economy/components/ManagePayments/interface";
import { PaymentStates } from "us.collection.economy/constants";
import { PaymentHelper } from "us.collection.economy/components/ManagePayments/Functions";
import { BoxIcons, IconTypes, ExclamationCircleFilled } from "us.icons";
import { getRouteUrl } from "us.helper";
import { useFormikContext } from "formik";
import { OCR_ITEM_TYPE_IDS } from "us.collection.economy/components/ManagePayments/Constants";

interface Props {
  data: any;
  priorityTag: IPaymentStates;
  onViewLinkedPayment: () => void;
  onDeletePayment: () => void;
  mainStatus: string;
}

const BaseHeader: React.FC<Props> = ({
  data,
  priorityTag,
  onViewLinkedPayment,
  onDeletePayment,
  mainStatus
}) => {
  const { t } = useTranslation();
  const [removePopoverStatus, setRemovePopoverStatus] =
    useState<boolean>(false);
  const { values } = useFormikContext<any>();
  const {
    paymentId,
    paymentAmount,
    returnedToDebtorSum,
    mappedSum,
    exceededSum,
    regDate,
    voucherDate,
    kid,
    pid,
    cid,
    voucherNo,
    caseNo,
    debtorName,
    creditorName,
    linkedPaymentId,
    isDeleteAvailable,
    isCaseClosed,
    isDeletionExpired,
    isManuallyAdded,
    fileName,
    itemTypeId
  } = data;
  const { amount, name, tagClass } = PaymentHelper.getPriorityStatusData(
    data,
    priorityTag
  );

  const customValidationMessage = useMemo(() => {
    if(fileName !== "Manual" && OCR_ITEM_TYPE_IDS.includes(itemTypeId)){
      return "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.PAYMENT_DELETION_IS_DISABLED_FOR_OCR_PAYMENTS";
    } else if(linkedPaymentId > 0 && mainStatus === PaymentStates.UNKNOWN){
      return "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.THIS_PAYMENT_HAS_BEEN_ALREADY_RESOLVED";
    }else if (!isManuallyAdded) {
      return "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.THIS_IS_NOT_REGISTERED_VIA_REGISTER_PAYMENTS";
    }else if (isDeletionExpired) {
      return "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.THE_PAYMENT_HAS_PASSED_THE_NO_OF_ALLOWED_DAYS_FOR_DELETION_AFTER_REGISTRATION";
    }else if (isCaseClosed) {
      return "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.THE_CASE_IS_CLOSED_PLEASE_REOPEN_AND_PERFORM_DELETION_AGAIN";
    } else if (!isDeleteAvailable) {
      return "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.THERE_IS_AN_OCCURRENCE_AND_THE_PAYMENT_IS_DISABLED";
    } else {
      return "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.PLEASE_REAPPORTION_IF_NECESSARY_AS_THERE_ARE_OTHER_CLAIM_ASSOCIATED_PAYMENTS";
    }
}, [isCaseClosed, isDeleteAvailable, isDeletionExpired, isManuallyAdded]);

const isPaymentDisabled = useMemo(() => {
  return (
    !isDeleteAvailable ||
    isCaseClosed ||
    isDeletionExpired ||
    !isManuallyAdded ||
    (linkedPaymentId > 0 && mainStatus === PaymentStates.UNKNOWN)
  );
}, [isCaseClosed, isDeleteAvailable, isDeletionExpired, isManuallyAdded]);

  return (
    <div className="">
      <div className="d-flex justify-content-between align-items-start">
        <div className="header-title">
          {`${t(
            "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.PAYMENT_ID"
          )}: ${paymentId}`}
          <span className="pl-3" data-testid="payment-amount">
            ({`${t("US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.AMOUNT")}: `}
            <$AmountLabel value={paymentAmount} />)
          </span>
        </div>
        <div className="d-flex align-items-center">
          <div className="text-nowrap" style={{ marginTop: -2 }}>
            {!isNaN(linkedPaymentId) && linkedPaymentId !== 0 && (
              <$Tooltip
                title={
                  <div className="d-flex">
                    {`${t(
                      "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.THIS_PAYMENT_LINKED_WITH"
                    )} : ${linkedPaymentId}`}
                  </div>
                }
              >
                <$Button
                  size="small"
                  type="link"
                  onClick={onViewLinkedPayment}
                  data-testid="remove-Btn"
                  icon={<BoxIcons type={IconTypes.BOX_ICON} name="linked" />}
                />
              </$Tooltip>
            )}
            <$Tooltip
              title={<div className="d-flex">{t(customValidationMessage)}</div>}
            >
              <$Popconfirm
                title={
                  <div>
                    <p style={{ width: "250px" }}>
                      {t(
                        "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.PLEASE_COMMENT_BELOW_AND_CONFIRM_THE_ACTION_REMOVE"
                      )}
                    </p>
                    <$TextArea
                      style={{ width: "250px" }}
                      required
                      placeholder={t("COMMON.COMMENT")}
                      name="deleteComment"
                      autoSize={{ minRows: 2, maxRows: 5 }}
                      maxLength={150}
                    />
                  </div>
                }
                onVisibleChange={(visible: any) => {
                  if (!visible) {
                    setRemovePopoverStatus(false);
                  }
                }}
                okButtonProps={{
                  type: "primary",
                  danger: true,
                  disabled: !values?.deleteComment,
                }}
                destroyTooltipOnHide
                placement="topLeft"
                onConfirm={() => {
                  onDeletePayment();
                  setRemovePopoverStatus(false);
                }}
                onCancel={() => {
                  setRemovePopoverStatus(false);
                }}
                okText={t("COMMON.REMOVE")}
                cancelText={t("COMMON.CANCEL")}
                visible={removePopoverStatus}
                icon={<ExclamationCircleFilled className="text-error" />}
              >
                <$Button
                  name="removeBtn"
                  type="link"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRemovePopoverStatus(!removePopoverStatus);
                  }}
                  size="small"
                  className="ml-1"
                  disabled={
                    isPaymentDisabled
                  }
                  data-testid="remove-btn"
                  icon={
                    <BoxIcons
                      type={IconTypes.BOX_ICON}
                      name="delete"
                      className={
                        isPaymentDisabled
                          ? "text-muted"
                          : "text-error"
                      }
                    />
                  }
                />
              </$Popconfirm>
            </$Tooltip>
          </div>
          <div className="header-tag d-flex align-items-end ml-3">
            {returnedToDebtorSum > 0 && priorityTag !== PaymentStates.RETURNED && (
              <$Tooltip
                title={
                  <div
                    className="d-flex justify-content-between"
                    data-testid="tooltip-return"
                  >
                    <span className="mr-5">
                      {t(
                        "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.RETURN_TO_DEBTOR"
                      )}
                    </span>
                    <span className="map-item-tag-amount">
                      <$AmountLabel value={returnedToDebtorSum} />
                    </span>
                  </div>
                }
              >
                <$Tag className="tag-status-returntodebtor header-tag-small" />
              </$Tooltip>
            )}
            {mappedSum > 0 && priorityTag !== PaymentStates.MAPPED && (
              <$Tooltip
                title={
                  <div
                    className="d-flex justify-content-between"
                    data-testid="tooltip-mapped"
                  >
                    <span className="mr-5">
                      {t("US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.MAPPED")}
                    </span>
                    <span className="map-item-tag-amount">
                      <$AmountLabel value={mappedSum} />
                    </span>
                  </div>
                }
              >
                <$Tag className="tag-status-mapped header-tag-small" />
              </$Tooltip>
            )}
            {exceededSum > 0 && priorityTag !== PaymentStates.EXCEEDED && (
              <$Tooltip
                title={
                  <div
                    className="d-flex justify-content-between"
                    data-testid="tooltip-exceeded"
                  >
                    <span className="mr-5">
                      {t("US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.EXCEEDED")}
                    </span>
                    <span className="map-item-tag-amount">
                      <$AmountLabel value={exceededSum} />
                    </span>
                  </div>
                }
              >
                <$Tag className="tag-status-exceeded header-tag-small" />
              </$Tooltip>
            )}
            <$Tag className={tagClass}>
              <div
                className="d-flex justify-content-between"
                data-testid="main-tag-amount"
              >
                <span className="mr-3">
                  {`${t(name)} ${
                    priorityTag === PaymentStates.UNKNOWN && linkedPaymentId > 0
                      ? `- ${t(
                          "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.RESOLVED"
                        )}`
                      : ""
                  }`}
                </span>
                {!(
                  priorityTag === PaymentStates.UNKNOWN && linkedPaymentId > 0
                ) && (
                  <span className="map-item-tag-amount">
                    <$AmountLabel value={amount} />
                  </span>
                )}
              </div>
            </$Tag>
          </div>
        </div>
      </div>
      <div className=" map-item-font-sm mt-1" data-testid="date-case-details">
        {`${t("US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.REGISTERED_DATE")}: `}
        <$DateLabel value={regDate} />
        {` | ${t("US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.VOUCHER")} : ${
          voucherNo && voucherNo > 0
            ? voucherNo
            : t("US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.NOT_AVAILABLE")
        }
                    - `}
        <$DateLabel value={voucherDate} />
        {" | "}
        <span>
          {`  ${t("US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.CASE_NO")}: `}

          {caseNo && caseNo > 0 && (
            <a
              href={"javascript:void(0)"}
              onClick={() => getRouteUrl.caseType(caseNo)}
            >
              {caseNo}
            </a>
          )}
          {!caseNo ||
            isNaN(caseNo) ||
            (!(caseNo > 0) &&
              t("US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.NOT_AVAILABLE"))}
        </span>
      </div>
      <div className="text-muted map-item-font-sm">
        {kid && `${t("US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.KID")}: ${kid}`}
        {kid && (cid || debtorName) && " | "}
        <span data-testid="debtor-details">
          {cid
            ? debtorName
              ? `${t("US.COLLECTION.COMMON:COMMON.DEBTOR")} : ${cid ?? ""}${
                  debtorName ? " - " + debtorName : ""
                } | `
              : `${t("US.COLLECTION.COMMON:COMMON.DEBTOR")} : ${
                  cid ? cid + " | " : ""
                }`
            : debtorName
            ? `${t("US.COLLECTION.COMMON:COMMON.DEBTOR")} : ${
                debtorName ? debtorName + " | " : ""
              }`
            : ""}
        </span>
        <span data-testid="creditor-details">
          {pid
            ? creditorName
              ? `${t("US.COLLECTION.COMMON:COMMON.CREDITOR")} : ${pid ?? ""}${
                  creditorName ? " - " + creditorName : ""
                }`
              : `${t("US.COLLECTION.COMMON:COMMON.CREDITOR")} : ${pid ?? ""}`
            : creditorName
            ? `${t("US.COLLECTION.COMMON:COMMON.CREDITOR")} : ${
                creditorName ? " - " + creditorName : ""
              }`
            : ""}
        </span>
      </div>
    </div>
  );
};

export default BaseHeader;
